"use client"

import { ampli } from "@/ampli"
import { JobTitleColorised } from "@/components/JobTitleColorised"
import { IconButton, Image, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { useSession } from "@/hooks/useSession"
import { ProCheck } from "@/icons/outline"
import { UserCheckIcon } from "@/icons/outline/UserCheckIcon"
import imageRow from "@/public/subscriptions/success-image-row.png"
import { BellIcon, LockOpenIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { ArrowRightIcon } from "@heroicons/react/24/solid"
import clsx from "clsx"
import { AnimatePresence, motion } from "motion/react"
import NextImage from "next/image"
import Link from "next/link"
import { FC, useEffect, useState } from "react"
import { ProBadge } from "./ProBadge"
import { usePathname } from "next/navigation"
const SubscriptionCheckoutSuccessFragment = graphql(`
  fragment SubscriptionCheckoutSuccess on JobPosting {
    id
    __typename
    ...JobTitleColorised
    coverImage {
      id
      thumbnailUrl144: thumbnailUrl(width: 144)
    }
  }
`)

interface Props {
  onClose: () => void
  jobPosting?: FragmentType<typeof SubscriptionCheckoutSuccessFragment> | null
}

const benefits = [
  {
    text: "Unlocked job board access",
    Icon: <LockOpenIcon className="size-[24px] stroke-fg-2" />,
  },
  {
    text: "Enhanced portfolio visibility",
    Icon: <UserCheckIcon className="size-[24px] stroke-fg-2" />,
  },
  {
    Icon: <BellIcon className="size-[24px] stroke-fg-2" />,
    text: "Instant job alerts available on iOS",
  },
]

const benefitsFadeInDuration = 1

export const SubscriptionSuccessStep: FC<Props> = ({ onClose, ...props }) => {
  const pathname = usePathname()
  const jobPosting = useFragment(SubscriptionCheckoutSuccessFragment, props.jobPosting)
  const session = useSession()
  const [step, setStep] = useState(1)

  useEffect(() => {
    if (step === 1) {
      const timeout = setTimeout(() => setStep(2), benefitsFadeInDuration * benefits.length * 1000 + 4000)
      return () => clearTimeout(timeout)
    }
  }, [step])

  const fadeVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.5 } },
    exit: { opacity: 0, transition: { duration: 0.5 } },
  }

  const links = [
    {
      title: "Explore the job board",
      caption: "You can apply to unlimited jobs",
      link: "/jobs",
      trackingAction: "browse-job-board",
    },
    {
      title: "Post a new project",
      caption: "Your portfolio gets more visibility",
      link: `/${session.data?.user.creative.handle}/projects/form`,
      trackingAction: "post-project",
    },
  ]

  const jobPostingImage = jobPosting?.coverImage?.thumbnailUrl144

  return (
    <div className={clsx("relative w-full", jobPosting ? "h-dvh sm:h-[613px]" : "h-dvh sm:h-[493px]")}>
      <div className="absolute right-4 top-4 z-20">
        <IconButton Icon={XMarkIcon} onClick={onClose} floating />
      </div>
      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div
            key="step-1"
            className="absolute inset-0"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit">
            <div
              className={clsx(
                "relative flex flex-col items-center justify-center",
                Boolean(jobPosting) ? "pb-[72px] pt-[114px]" : "py-[72px]"
              )}>
              <div className="absolute left-0 right-0 top-0 z-0 h-full w-full">
                <video
                  src="/subscriptions/success-video.mp4"
                  autoPlay
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="absolute bottom-[-1px] z-10 h-full w-full bg-gradient-to-b from-bg-1/0 via-bg-1/0 to-bg-1" />
              <div className="relative z-10 flex w-full flex-row items-center justify-center">
                <ProBadge />
              </div>
              <div className="relative z-10 mt-4 max-w-[304px]">
                <Text variant="h6" className="text-center text-fg-2">
                  Welcome to NOVA Pro.
                </Text>
                <Text variant="h5" className="mt-1 text-center">
                  More visibility. More jobs.
                </Text>
                <Text variant="h5" className="text-center">
                  More opportunity.
                </Text>
              </div>
            </div>
            <div
              className={clsx(
                "flex w-full flex-col gap-4 px-[54px] pb-[54px]",
                Boolean(jobPosting) ? "mt-[65px]" : "mt-8"
              )}>
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex flex-row items-center justify-between opacity-0"
                  initial={{ opacity: 0, scale: 1.2 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * benefitsFadeInDuration }}>
                  <div className="flex flex-row items-center gap-3">
                    {benefit.Icon}
                    <Text>{benefit.text}</Text>
                  </div>
                  <div className="flex size-[40px] items-center justify-center rounded-full bg-bg-2">
                    <ProCheck className="size-[24px]" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="step-2"
            className="absolute inset-0"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit">
            <NextImage src={imageRow} alt="Success image row" className="w-full pb-[70px] pt-[90px] sm:pb-0 sm:pt-8" />
            <Text variant="h6" className="text-center">
              Explore your{" "}
              <Text element="span" variant="h6" className="text-primary-3">
                Pro
              </Text>{" "}
              benefits
            </Text>
            <div className="mt-6 flex flex-col gap-3 px-8 pb-8">
              {Boolean(jobPosting) && (
                <Link
                  href={`/jobs/${jobPosting?.id}`}
                  onClick={() => {
                    ampli.benefitsModalInteraction({
                      action: "continue-applying",
                    })
                    if (pathname === `/jobs/${jobPosting?.id}`) {
                      onClose()
                    }
                  }}>
                  <div className="flex flex-col rounded-lg border border-bg-3 p-6 hover:shadow-[0_4px_24px_0_rgba(0,0,0,0.1)]">
                    <Text variant="body1">Continue applying</Text>
                    <div className="mt-3 flex flex-row items-center justify-between">
                      {jobPostingImage && (
                        <div className="relative mr-3 flex size-[48px] shrink-0 overflow-hidden rounded-md bg-bg-3">
                          <Image
                            srcSetUrls={{
                              sm: jobPostingImage,
                            }}
                            alt="Cover image"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}
                      <JobTitleColorised jobPost={jobPosting} maxLines={2} className="pr-4" />
                      <ArrowRightIcon className="size-[24px] flex-shrink-0" />
                    </div>
                  </div>
                </Link>
              )}
              {links.map(link => (
                <Link
                  href={link.link}
                  key={link.link}
                  onClick={() => {
                    ampli.benefitsModalInteraction({
                      action: link.trackingAction,
                    })
                    if (pathname === link.link) {
                      onClose()
                    }
                  }}>
                  <div className="flex flex-row items-center justify-between rounded-lg border border-bg-3 p-6 hover:shadow-[0_4px_24px_0_rgba(0,0,0,0.1)]">
                    <div>
                      <Text variant="body1">{link.title}</Text>
                      <Text variant="body2" className="text-pro-gradient-dark">
                        {link.caption}
                      </Text>
                    </div>
                    <ArrowRightIcon className="size-[24px]" />
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
